import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") ?? "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("Missing reset token. Please use the link from your email.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-full max-w-sm bg-card rounded-lg shadow p-8">
          <h1 className="text-2xl font-bold mb-4">Invalid reset link</h1>
          <p className="text-sm text-muted-foreground mb-4">
            This reset link is invalid or has expired. Please request a new one.
          </p>
          <Link to="/forgot-password" className="text-primary hover:underline text-sm">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-sm bg-card rounded-lg shadow p-8">
        <h1 className="text-2xl font-bold mb-2">Set new password</h1>
        {success ? (
          <>
            <p className="text-sm text-green-600 mb-4">
              Password updated successfully! Redirecting to login…
            </p>
            <p className="text-sm text-center">
              <Link to="/login" className="text-primary hover:underline">Go to login</Link>
            </p>
          </>
        ) : (
          <>
            {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="password" placeholder="New password (min. 8 characters)"
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                required minLength={8}
                className="w-full border rounded px-3 py-2 text-sm bg-background"
              />
              <input
                type="password" placeholder="Confirm new password"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full border rounded px-3 py-2 text-sm bg-background"
              />
              <button type="submit" className="w-full bg-primary text-primary-foreground rounded py-2 text-sm hover:opacity-90">
                Set new password
              </button>
            </form>
            <p className="text-sm text-muted-foreground mt-4 text-center">
              <Link to="/login" className="text-primary hover:underline">Back to login</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
